import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, Package, BarChart3, Eye, ShoppingCart, Zap,
  ArrowRight, CheckCircle, LineChart, Store
} from 'lucide-react';
import { CILogo } from '../components/CILogo';

export default function BrandsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B1120]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B1120]/95 backdrop-blur-sm border-b border-[#22304A]">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <CILogo size={32} variant="primary" theme="dark" />
              <span className="font-semibold text-lg text-white">Commerce Index</span>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/extension')} className="text-[#94A3B8] hover:text-white transition-colors">
                CI Shopper
              </button>
              <button onClick={() => navigate('/proof')} className="text-[#94A3B8] hover:text-white transition-colors">
                CI Proof
              </button>
              <button onClick={() => navigate('/signals')} className="text-[#94A3B8] hover:text-white transition-colors">
                Signals
              </button>
              <button onClick={() => navigate('/docs')} className="text-[#94A3B8] hover:text-white transition-colors">
                Docs
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-20">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: 'radial-gradient(60% 60% at 50% 30%, rgba(62,241,199,0.18) 0%, rgba(62,241,199,0) 60%)'
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-6">
              <Store className="w-3 h-3 mr-1" />
              Coming Soon
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              CI for Brands —
              <span className="text-[#3EF1C7]"> Commerce Index for Your Own Store</span>
            </h1>
            <p className="text-xl text-[#94A3B8] mb-8 leading-relaxed">
              Turn the view and purchase events you already send to Commerce Index into 
              brand-level dashboards, product insights, and simple Commerce Index signals 
              you can actually act on.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90 text-lg px-8 py-6"
                data-testid="brands-cta-waitlist"
              >
                Join the CI for Brands Waitlist
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Link to="/proof">
                <Button 
                  variant="outline" 
                  className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B] text-lg px-8 py-6"
                >
                  Learn About CI Proof
                </Button>
              </Link>
            </div>
            <p className="text-sm text-[#64748B] mt-6">
              CI for Brands is in active development. Early access will roll out to brands that 
              have CI Proof integrated first.
            </p>
          </div>
        </div>
      </section>

      {/* What CI for Brands Will Offer */}
      <section className="py-20 bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              What CI for Brands Will Do for You
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-[#1E293B] border-[#334155]">
              <CardHeader>
                <BarChart3 className="w-10 h-10 text-[#3EF1C7] mb-4" />
                <CardTitle className="text-white">Brand Activity Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm">
                  See total views, purchases, and conversion rate for your brand over time, with simple 
                  trends vs previous periods — without building your own event aggregation.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1E293B] border-[#334155]">
              <CardHeader>
                <Package className="w-10 h-10 text-[#7A5CFF] mb-4" />
                <CardTitle className="text-white">Product-Level Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm">
                  Quickly spot which products are hot, steady, or cold based on real traffic and purchase 
                  activity, not just intuition or ad performance.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1E293B] border-[#334155]">
              <CardHeader>
                <TrendingUp className="w-10 h-10 text-[#FFD166] mb-4" />
                <CardTitle className="text-white">Contextual Signals</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm">
                  Lean on CI Signals to get a one-line summary and a few key signals for each product or 
                  for your brand — designed to clear the noise, not add more dashboards.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1E293B] border-[#334155]">
              <CardHeader>
                <Eye className="w-10 h-10 text-[#3EF1C7] mb-4" />
                <CardTitle className="text-white">Built on Proof Events</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm">
                  CI for Brands runs on top of the view and purchase events you already send to Commerce Index 
                  for Proof and the extension. No extra tracking required.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Coming Soon / Early Access */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#1E293B] to-[#0D1628] rounded-2xl p-12 border border-[#334155]">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                We are Building CI for Brands with Early Partners
              </h2>
              <p className="text-lg text-[#94A3B8] mb-6">
                If you want a better way to understand how your brand and products are really performing — 
                beyond ad dashboards and disconnected analytics — CI for Brands is for you.
              </p>
              <p className="text-[#E2E8F0] mb-4">
                Early access will start with a small circle of brands who:
              </p>
              <ul className="text-[#94A3B8] mb-8 space-y-2">
                <li className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#3EF1C7]" />
                  have CI Proof integrated, and
                </li>
                <li className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#3EF1C7]" />
                  are willing to give feedback on the first version of CI dashboards and Signals.
                </li>
              </ul>
              <Button 
                className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90 text-lg px-8 py-6"
                data-testid="brands-cta-waitlist-bottom"
              >
                Join the Waitlist for CI for Brands
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Stack Preview */}
      <section className="py-20 bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Part of the Commerce Index Stack
            </h2>
            <p className="text-lg text-[#94A3B8]">
              CI for Brands connects to the same event graph powering all Commerce Index products.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/proof">
              <Card className="bg-[#1E293B] border-[#334155] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer h-full">
                <CardHeader>
                  <Eye className="w-8 h-8 text-[#3EF1C7] mb-2" />
                  <CardTitle className="text-white text-lg">CI Proof</CardTitle>
                  <Badge className="w-fit bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30">Live</Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-[#94A3B8] text-sm">
                    Real-time social proof widget for your PDPs
                  </p>
                </CardContent>
              </Card>
            </Link>
            
            <Card className="bg-[#1E293B] border-[#334155] border-2 border-[#FFD166]/30">
              <CardHeader>
                <LineChart className="w-8 h-8 text-[#FFD166] mb-2" />
                <CardTitle className="text-white text-lg">CI for Brands</CardTitle>
                <Badge className="w-fit bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30">Coming Soon</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm">
                  Dashboards and insights for brand teams
                </p>
              </CardContent>
            </Card>
            
            <Link to="/signals">
              <Card className="bg-[#1E293B] border-[#334155] hover:border-[#7A5CFF]/50 transition-colors cursor-pointer h-full">
                <CardHeader>
                  <Zap className="w-8 h-8 text-[#7A5CFF] mb-2" />
                  <CardTitle className="text-white text-lg">CI Signals</CardTitle>
                  <Badge className="w-fit bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30">API</Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-[#94A3B8] text-sm">
                    API-first commerce intelligence for developers
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#22304A]">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <CILogo size={32} variant="primary" theme="dark" />
              <span className="font-semibold text-white">Commerce Index</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-[#94A3B8]">
              <button onClick={() => navigate('/extension')} className="hover:text-white transition-colors">CI Shopper</button>
              <button onClick={() => navigate('/proof')} className="hover:text-white transition-colors">CI Proof</button>
              <button onClick={() => navigate('/signals')} className="hover:text-white transition-colors">CI Signals</button>
              <button onClick={() => navigate('/docs')} className="hover:text-white transition-colors">Docs</button>
              <button onClick={() => navigate('/contact')} className="hover:text-white transition-colors">Contact</button>
            </div>
          </div>
          <p className="text-center text-[#64748B] text-sm mt-8">
            © 2024 Commerce Index. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
